import { useReducer, useCallback, useEffect } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: convex/_generated is the documented exception per CLAUDE.md
import { toast } from 'sonner';
import type { IAutomationFilterRule } from '@/@types/automation';

// LOCAL_TYPE_JUSTIFIED: Hook-internal reducer state, not reused outside
interface IAutomationState {
  autoCreateLeads: boolean;
  filters: IAutomationFilterRule[];
  isSaving: boolean;
  isDirty: boolean;
}

type TAction =
  | { type: 'INIT'; autoCreateLeads: boolean; filters: IAutomationFilterRule[] }
  | { type: 'SET_AUTO_CREATE'; value: boolean }
  | { type: 'ADD_FILTER' }
  | { type: 'UPDATE_FILTER'; index: number; rule: IAutomationFilterRule }
  | { type: 'DELETE_FILTER'; index: number }
  | { type: 'SET_SAVING'; value: boolean }
  | { type: 'MARK_CLEAN' };

function reducer(state: IAutomationState, action: TAction): IAutomationState {
  switch (action.type) {
    case 'INIT':
      return {
        autoCreateLeads: action.autoCreateLeads,
        filters: action.filters,
        isSaving: false,
        isDirty: false,
      };
    case 'SET_AUTO_CREATE':
      return { ...state, autoCreateLeads: action.value, isDirty: true };
    case 'ADD_FILTER':
      return {
        ...state,
        filters: [...state.filters, { senderEmail: '', subjectPattern: '' }],
        isDirty: true,
      };
    case 'UPDATE_FILTER':
      return {
        ...state,
        filters: state.filters.map((f, i) => (i === action.index ? action.rule : f)),
        isDirty: true,
      };
    case 'DELETE_FILTER':
      return {
        ...state,
        filters: state.filters.filter((_, i) => i !== action.index),
        isDirty: true,
      };
    case 'SET_SAVING':
      return { ...state, isSaving: action.value };
    case 'MARK_CLEAN':
      return { ...state, isDirty: false };
  }
}

export function useAutomationSettings() {
  const settings = useQuery(api.automationSettings.get);
  const upsert = useMutation(api.automationSettings.upsert);
  const [state, dispatch] = useReducer(reducer, {
    autoCreateLeads: false,
    filters: [],
    isSaving: false,
    isDirty: false,
  });

  useEffect(() => {
    if (settings) {
      dispatch({
        type: 'INIT',
        autoCreateLeads: settings.autoCreateLeads,
        filters: settings.filters,
      });
    }
  }, [settings]);

  const setAutoCreate = useCallback((value: boolean) => {
    dispatch({ type: 'SET_AUTO_CREATE', value });
  }, []);

  const addFilter = useCallback(() => {
    dispatch({ type: 'ADD_FILTER' });
  }, []);

  const updateFilter = useCallback((index: number, rule: IAutomationFilterRule) => {
    dispatch({ type: 'UPDATE_FILTER', index, rule });
  }, []);

  const deleteFilter = useCallback((index: number) => {
    dispatch({ type: 'DELETE_FILTER', index });
  }, []);

  const save = useCallback(async () => {
    dispatch({ type: 'SET_SAVING', value: true });
    try {
      const validFilters = state.filters.filter(
        f =>
          (f.senderEmail && f.senderEmail.trim()) || (f.subjectPattern && f.subjectPattern.trim())
      );
      await upsert({ autoCreateLeads: state.autoCreateLeads, filters: validFilters });
      dispatch({ type: 'MARK_CLEAN' });
      toast.success('Automation settings saved');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      dispatch({ type: 'SET_SAVING', value: false });
    }
  }, [state.autoCreateLeads, state.filters, upsert]);

  return {
    ...state,
    isLoading: settings === undefined,
    hasFilters: state.filters.some(
      f => (f.senderEmail && f.senderEmail.trim()) || (f.subjectPattern && f.subjectPattern.trim())
    ),
    setAutoCreate,
    addFilter,
    updateFilter,
    deleteFilter,
    save,
  };
}
